import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBarAuth from "../../../../components/navbar/navbarAuth";
import AlertContext from "../../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../../Files/BaseURL";
import { FaEye, FaEyeSlash } from "react-icons/fa";

function SignInForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (password.length > 30) {
      errors.push("Password must not exceed 30 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    if (!/[!@#$%^&*]/.test(password)) {
      errors.push("Password must contain at least one special character (!@#$%^&*)");
    }
    return errors;
  };

  const checkPasswordRequirement = (password, requirement) => {
    switch (requirement) {
      case "At least 8 characters long":
        return password.length >= 8;
      case "Maximum 30 characters":
        return password.length <= 30;
      case "At least one uppercase letter":
        return /[A-Z]/.test(password);
      case "At least one lowercase letter":
        return /[a-z]/.test(password);
      case "At least one number":
        return /[0-9]/.test(password);
      case "At least one special character (!@#$%^&*)":
        return /[!@#$%^&*]/.test(password);
      default:
        return false;
    }
  };

  const passwordRequirements = [
    "At least 8 characters long",
    "Maximum 30 characters",
    "At least one uppercase letter",
    "At least one lowercase letter",
    "At least one number",
    "At least one special character (!@#$%^&*)",
    "Passwords must match"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailError = validateEmail(email);
    const passwordErrors = validatePassword(password);
    let confirmPasswordError = "";

    if (!confirmPassword) {
      confirmPasswordError = "Please confirm your password";
    } else if (password !== confirmPassword) {
      confirmPasswordError = "Passwords do not match";
    }

    setErrors({
      email: emailError,
      password: passwordErrors.length > 0 ? passwordErrors[0] : "",
      confirmPassword: confirmPasswordError,
    });

    if (emailError || passwordErrors.length > 0 || confirmPasswordError) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BaseURL}/api/auth/createuser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Email: email, Password: password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showAlert(errorData.error || "Login Error Occurred", "danger");
        setIsLoading(false);
        return;
      }
      const data = await response.json();
      showAlert("Verification Email sent to you", "success");
      sessionStorage.setItem("signtoken", data.AuthToken);
      navigate("/auth-verify-otp");
    } catch (error) {
      showAlert("An error occurred, please try again later", "danger");
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavBarAuth />
      <div className="bg-gray-100">
        <div className="w-[15%] h-2 bg-primarygreen rounded-r-full"></div>
      </div>
      <div className="flex justify-center items-center bg-gray-100 py-20">
        <div className="mx-6 bg-white p-8 rounded-lg shadow-md w-[90%] md:w-[70%] lg:w-[35%]">
          <h2 className="text-2xl font-pop font-bold mb-8">
            Create an account
          </h2>
          <p className="text-gray-600 font-pop font-bold mb-8 max-w-sm">
            To sign up with Paycott enter your email
          </p>

          <div className="mb-4">
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: "" }));
              }}
              className={`w-full px-6 border-2 ${errors.email ? "border-red-500" : "border-transparent"
                } font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1 ml-4">{errors.email}</p>
            )}
          </div>

          <div className="mb-4 relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: "" }));
              }}
              placeholder="Password"
              className={`w-full px-6 border-2 ${errors.password ? "border-red-500" : "border-transparent"
                } font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
            </button>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1 ml-4">
                {errors.password}
              </p>
            )}
          </div>

          <div className="mb-4 relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirm-password"
              name="confirm-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }}
              placeholder="Confirm password"
              className={`w-full px-6 border-2 ${errors.confirmPassword ? "border-red-500" : "border-transparent"
                } font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
            </button>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1 ml-4">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <div className="mb-4 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold mb-2">Password Requirements:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              {passwordRequirements.map((requirement, index) => (
                <li key={index} className="flex items-center">
                  <span className={`mr-2 ${requirement === "Passwords must match"
                    ? (password && confirmPassword && password === confirmPassword ? 'text-green-500' : 'text-gray-400')
                    : (checkPasswordRequirement(password, requirement) ? 'text-green-500' : 'text-gray-400')
                    }`}>
                    {requirement === "Passwords must match"
                      ? (password && confirmPassword && password === confirmPassword ? '✓' : '○')
                      : (checkPasswordRequirement(password, requirement) ? '✓' : '○')}
                  </span>
                  {requirement}
                </li>
              ))}
            </ul>
          </div>

          <button
            className="my-4 w-full bg-green-500 hover:bg-transparent  hover:bg-white-500 font-semibold hover:text-black font-pop text-xl py-3 px-4 border border-black-500 hover:border-green-500 rounded-full  text-white animate__animated animate__backInUp "
            onClick={handleSubmit}
          >
            {isLoading ? "Loading..." : "Next"}
          </button>
          <p className="my-2 font-pop text-center">
            Already have account? Log In
            <span className="text-primarygreen font-medium">
              <Link to={"/signin"}> here</Link>
            </span>
          </p>
        </div>
      </div>
    </>
  );
}

export default SignInForm;
